import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import {
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
  filter,
} from 'rxjs';
import * as AlertActions from './alert.actions';
import { selectAlertConfig } from './alert.selectors';
import {
  Alert,
  PersistenceLevel,
} from '@surface-elements/shared/alert-system/domain';

@Injectable()
export class AlertEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);

  addAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlertActions.addAlert),
      withLatestFrom(this.store.select(selectAlertConfig)),
      map(([action, config]) => {
        const alert: Alert = {
          ...action.alert,
          id: this.generateId(),
          timestamp: new Date(),
          duration: action.alert.duration ?? config.defaultDuration,
        };
        return AlertActions.addAlertSuccess({ alert });
      }),
    ),
  );

  autoDismissAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlertActions.addAlertSuccess),
      filter(({ alert }) => alert.dismissible && !!alert.duration),
      mergeMap(({ alert }) =>
        timer(alert.duration!).pipe(
          map(() => AlertActions.removeAlert({ id: alert.id })),
        ),
      ),
    ),
  );

  persistAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlertActions.addAlertSuccess),
        filter(
          ({ alert }) =>
            alert.persistence !== PersistenceLevel.NONE &&
            alert.persistence !== undefined,
        ),
        tap(({ alert }) => {
          this.persistAlertToStorage(alert);
        }),
      ),
    { dispatch: false },
  );

  removeAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlertActions.removeAlert),
      switchMap(({ id }) => {
        this.removeAlertFromStorage(id);
        return of(AlertActions.removeAlertSuccess({ id }));
      }),
    ),
  );

  clearAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlertActions.clearAlerts),
      tap(() => {
        this.clearStoredAlerts();
      }),
      map(() => AlertActions.clearAlertsSuccess()),
    ),
  );

  private generateId(): string {
    return `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private persistAlertToStorage(alert: Alert): void {
    const storageKey =
      alert.persistence === PersistenceLevel.SESSION
        ? 'session-alerts'
        : 'critical-alerts';
    const storage =
      alert.persistence === PersistenceLevel.SESSION
        ? sessionStorage
        : localStorage;

    const existingAlerts = JSON.parse(storage.getItem(storageKey) || '[]');
    existingAlerts.push(alert);
    storage.setItem(storageKey, JSON.stringify(existingAlerts));
  }

  private removeAlertFromStorage(id: string): void {
    ['session-alerts', 'critical-alerts'].forEach(key => {
      [sessionStorage, localStorage].forEach(storage => {
        const alerts = JSON.parse(storage.getItem(key) || '[]');
        const filtered = alerts.filter((a: Alert) => a.id !== id);
        storage.setItem(key, JSON.stringify(filtered));
      });
    });
  }

  private clearStoredAlerts(): void {
    ['session-alerts', 'critical-alerts'].forEach(key => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  }
}
